// =============================================================================
// Rolled — unified site app entry point. ONE bundle for every site. Picks the
// front-of-house or back-of-house app from the active site's role (config) and
// mounts it once. The site itself is chosen by window.__SITE_KEY in index.html.
// =============================================================================
// FIRST import, on purpose: inside the card builder's frame (?preview=till) it closes
// the page off (storage, network) before any other module below is evaluated. It does
// nothing anywhere else. See lib/tillPreviewGuard.js.
import { TILL_PREVIEW_DOOR } from "./lib/tillPreviewGuard.js";
import React from "react";
import * as ReactDOM from "react-dom";
import { getActiveSite } from "./config/sites.js";
import { confirmedWrite } from "./lib/confirmedWrite.js";
import { FohApp } from "./foh.jsx";
import { BohApp } from "./boh-kitchen.jsx";
import { ReordersApp } from "./reorders.jsx";
import { mountTillPreview } from "./tillPreview.jsx";

const SITE = getActiveSite();

// Standalone Reorders page — a BOH owner opens <host>/?view=reorders to mark
// flagged items as reordered. Chosen here, before the normal FOH/BOH mount, so
// it is its own screen rather than a tile inside either app. The site still
// comes from the host, so nothing about site resolution changes.
const IS_REORDERS = (() => {
  try { return new URLSearchParams(window.location.search).get("view") === "reorders"; }
  catch (e) { return false; }
})();

// Dev surface for the migration build-out (harmless in production).
if (typeof window !== "undefined") window.__rolled = { site: SITE, confirmedWrite };

// Back-of-house (kitchen) loads its own stylesheet; front-of-house sites never
// request it, so their look can't be affected by kitchen CSS. A closed site
// mounts no app, so it never needs it (and it must not restyle the closure screen).
// Nor does the card builder's till preview door (no app either).
if (!TILL_PREVIEW_DOOR && SITE && SITE.isBOH && !SITE.closed && !IS_REORDERS && typeof document !== "undefined") {
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = "kitchen.css";
  document.head.appendChild(link);
}

// A closed site never mounts an app. Anyone reaching arndale.eatrolled.com or
// mcr-kitchen.eatrolled.com (or ?site=arndale / ?site=mcr) gets a dead end rather
// than a working app — otherwise they could file an EOD, confirm a forecast, mark
// sell-outs or raise stock requests against a site that will never trade, and that
// data flows into EOD history, forecasts and the nightly audits. Rendered before
// mount so no PIN prompt, fetch or poll from the app ever fires.
// `movedTo` (the kitchen: its team now works at Trafford) adds one line and a
// link to where the work went — a link staff tap, never an automatic redirect.
// (The card builder's till preview door draws its own screen instead: see below.)
if (!TILL_PREVIEW_DOOR && SITE && SITE.closed && typeof document !== "undefined") {
  const r = document.getElementById("root");
  if (r) {
    const moved = SITE.movedTo && SITE.movedTo.url && SITE.movedTo.label ? SITE.movedTo : null;
    // An old reorder-email link (<closed host>/?view=reorders) keeps its page.
    const movedHref = moved ? moved.url + (IS_REORDERS ? "?view=reorders" : "") : "";
    r.innerHTML =
      '<div style="min-height:100vh;display:flex;align-items:center;justify-content:center;' +
      'background:#00392C;color:#FEFAF0;font-family:Manrope,system-ui,sans-serif;padding:32px;text-align:center">' +
      '<div style="max-width:30rem">' +
      '<div style="font-size:2rem;font-weight:800;letter-spacing:-0.02em">' +
      (SITE.closedLabel || SITE.name) + ' has closed</div>' +
      (moved
        ? '<p style="font-size:1.05rem;line-height:1.6;opacity:0.85;margin:16px 0 24px">Use the ' +
          moved.label + ' app from now on.</p>' +
          '<a href="' + movedHref + '" style="display:inline-block;background:#D4FB6F;color:#00392C;' +
          'font-weight:800;font-size:1.05rem;text-decoration:none;padding:14px 28px;border-radius:999px">' +
          'Open the ' + moved.label + ' app</a>'
        : '') +
      '</div></div>';
  }
}

const App = IS_REORDERS ? ReordersApp : (SITE && SITE.isBOH ? BohApp : FohApp);

// NOTE: do NOT throw to stop the mount. index.html:837 installs a window.onerror
// handler that WIPES #root and prints "JS Error" — so throwing here replaces the
// closure notice with a stack trace, which is exactly what staff must not see.
// The till preview door (a framed ?preview=till, see tillPreview.jsx) draws the real
// till screens for the card builder instead of the app: no PIN, no polls, no app.
try {
  if (TILL_PREVIEW_DOOR) mountTillPreview(document.getElementById("root"));
  else if (!(SITE && SITE.closed)) ReactDOM.render(React.createElement(App), document.getElementById("root"));
} catch (e) {
  const r = document.getElementById("root");
  if (r) r.innerHTML = '<div style="color:#FEFAF0;padding:40px;font-family:monospace"><h2>Error loading app</h2><pre>' + (e && e.message) + "</pre></div>";
}
